import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { LogoMark } from "@/components/app/Brand";
import HeroBackdrop from "@/components/app/HeroBackdrop";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const VerifyEmail = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  // states: 'verifying' | 'ok' | 'already' | 'error' | 'missing'
  const [status, setStatus] = useState(token ? "verifying" : "missing");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        if (cancelled) return;
        setStatus(data.already_verified ? "already" : "ok");
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err.response?.data?.detail || "Verification failed");
        setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0D1117]">
      <div className="hidden lg:block relative border-r border-[#1F2937] bg-[#060D1A] text-white overflow-hidden">
        <HeroBackdrop />
      </div>
      <div className="flex flex-col p-6 sm:p-10">
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm" data-testid="verify-email-page">
            <div className="mb-8">
              <LogoMark />
            </div>

          {status === "verifying" && (
            <div data-testid="verify-loading">
              <Loader2 className="w-6 h-6 text-[#002FA7] animate-spin mb-3" />
              <h1 className="font-display font-black text-2xl text-white tracking-tighter">Confirming your email…</h1>
            </div>
          )}

          {status === "ok" && (
            <div data-testid="verify-success">
              <div className="flex items-center gap-2 text-emerald-700 mb-3">
                <CheckCircle2 className="w-6 h-6" />
                <h1 className="font-display font-black text-2xl text-white tracking-tighter">Email confirmed.</h1>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Thanks — your inbox is now connected to your CombinedWork account. You&apos;ll receive team invites, password resets, and PMO alerts here.
              </p>
              <div className="mt-8">
                <Link to="/dashboard" className="text-sm text-[#002FA7] font-semibold" data-testid="verify-open-dashboard">
                  Open dashboard →
                </Link>
              </div>
            </div>
          )}

          {status === "already" && (
            <div data-testid="verify-already">
              <div className="flex items-center gap-2 text-emerald-700 mb-3">
                <CheckCircle2 className="w-6 h-6" />
                <h1 className="font-display font-black text-2xl text-white tracking-tighter">Already verified.</h1>
              </div>
              <p className="text-sm text-slate-400">This email was confirmed previously — nothing more to do.</p>
              <div className="mt-8">
                <Link to="/dashboard" className="text-sm text-[#002FA7] font-semibold">Open dashboard →</Link>
              </div>
            </div>
          )}

          {status === "missing" && (
            <div data-testid="verify-missing">
              <div className="flex items-center gap-2 text-rose-700 mb-3">
                <AlertTriangle className="w-6 h-6" />
                <h1 className="font-display font-black text-2xl text-white tracking-tighter">Missing token</h1>
              </div>
              <p className="text-sm text-slate-400">Open the link from your verification email.</p>
            </div>
          )}

          {status === "error" && (
            <div data-testid="verify-error">
              <div className="flex items-center gap-2 text-rose-700 mb-3">
                <AlertTriangle className="w-6 h-6" />
                <h1 className="font-display font-black text-2xl text-white tracking-tighter">Couldn&apos;t verify</h1>
              </div>
              <p className="text-sm text-slate-400">{errorMsg}</p>
              <p className="text-xs text-gray-500 mt-3">
                Sign in and click &ldquo;Resend verification email&rdquo; in the banner to get a fresh link.
              </p>
              <div className="mt-8">
                <Link to="/auth" className="text-sm text-[#002FA7] font-semibold">Back to sign in</Link>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
